export const INVOICE_CHANNELS = ['email', 'peppol'] as const;
export type InvoiceChannel = (typeof INVOICE_CHANNELS)[number];

export const INVOICE_CHANNEL_LABELS: Record<InvoiceChannel, string> = {
  email: 'E-mail',
  peppol: 'Peppol',
};

export const INVOICE_CHANNEL_OPTIONS: { key: InvoiceChannel; name: string }[] = INVOICE_CHANNELS.map((key) => ({
  key,
  name: INVOICE_CHANNEL_LABELS[key],
}));

/**
 * Invoices are always available in the customer portal. Email and Peppol are
 * optional extra send channels.
 */
export function invoiceChannelsFromCustomer(customer: {
  peppolEnabled: boolean;
  emailEnabled: boolean;
}): InvoiceChannel[] {
  const channels: InvoiceChannel[] = [];
  if (customer.emailEnabled) {
    channels.push('email');
  }
  if (customer.peppolEnabled) {
    channels.push('peppol');
  }
  return channels;
}

export function customerFieldsFromInvoiceChannels(channels: InvoiceChannel[]): {
  peppolEnabled: boolean;
  emailEnabled: boolean;
} {
  return {
    peppolEnabled: channels.includes('peppol'),
    emailEnabled: channels.includes('email'),
  };
}

export function formatInvoiceChannels(channels: InvoiceChannel[]): string {
  if (channels.length === 0) {
    return 'Klantenportaal';
  }
  return channels.map((channel) => INVOICE_CHANNEL_LABELS[channel]).join(', ');
}

export function defaultInvoiceChannelsForCountry(country: string | undefined): InvoiceChannel[] {
  return country === 'BE' ? ['peppol'] : ['email'];
}

export function invoiceSendDestinations(customer: { emailEnabled: boolean; peppolEnabled: boolean }): string[] {
  const destinations = ['Klantenportaal'];
  if (customer.emailEnabled) {
    destinations.push('E-mail');
  }
  if (customer.peppolEnabled) {
    destinations.push('Peppol');
  }
  return destinations;
}

export function shouldEmailCustomerDocuments(customer: { emailEnabled: boolean }): boolean {
  return customer.emailEnabled;
}

export function selectInvoiceEmailContacts<T extends { id: number; shouldReceiveInvoices: boolean }>(
  contacts: T[],
  contactIds?: number[],
): T[] {
  if (contactIds) {
    const allowed = new Set(contactIds);
    return contacts.filter((contact) => allowed.has(contact.id));
  }
  return contacts.filter((contact) => contact.shouldReceiveInvoices);
}

export function resolveDocumentSendFlags(
  customer: { emailEnabled: boolean; peppolEnabled: boolean },
  options?: { sendEmail?: boolean; sendPeppol?: boolean },
): { sendEmail: boolean; sendPeppol: boolean } {
  return {
    sendEmail: options?.sendEmail ?? customer.emailEnabled,
    sendPeppol: options?.sendPeppol ?? customer.peppolEnabled,
  };
}
